import { Box, Stack, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  RegulationFeedItem as RegulationFeedItemInterface,
  RegulationFeedList,
} from '../../utilities/RegulationFeedData';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import RegulationFeedItem from './RegulationFeedItem';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fi';
import { v4 as uuidv4 } from 'uuid';

interface RegulationFeedProps {
  readonly feed: RegulationFeedList;
  readonly category?: string;
}

function filterFeedByCategory(
  feed: RegulationFeedList,
  category?: string,
): RegulationFeedList {
  if (category) {
    return feed.filter((item) => {
      return item.category === category;
    });
  }

  return feed;
}

interface GroupedRegulationFeedListItem {
  date: string;
  items: RegulationFeedList;
}

interface GroupedRegulationFeedList
  extends Array<GroupedRegulationFeedListItem> {}

/**
 * Group feed items by date in descending order. The items in each group are
 * sorted by time in descending order.
 */
function groupByDate(feed: RegulationFeedList) {
  const sortedByTime = feed.sort(
    (a: RegulationFeedItemInterface, b: RegulationFeedItemInterface) => {
      return dayjs(b.pubDate).diff(dayjs(a.pubDate));
    },
  );

  const groups: GroupedRegulationFeedList = sortedByTime.reduce(
    (acc, item: RegulationFeedItemInterface) => {
      const date = dayjs(item.pubDate).format('YYYY-MM-DD');

      let dateGroup: GroupedRegulationFeedListItem | undefined = acc.find(
        (group: GroupedRegulationFeedListItem) => {
          return group.date === date;
        },
      );

      if (!dateGroup) {
        const newGroup: GroupedRegulationFeedListItem = { date, items: [] };
        const index = acc.push(newGroup) - 1;
        dateGroup = acc[index];
      }

      dateGroup?.items?.push(item);
      return acc;
    },
    [] as GroupedRegulationFeedList,
  );

  const groupsInOrder = groups.sort((a, b) => {
    return dayjs(b.date).diff(dayjs(a.date));
  });

  return groupsInOrder;
}

/**
 * Get a title for a date. The title is either "Today", "Yesterday" or "X days ago".
 */
function getDateTitle(date: string): string {
  const differenceInFullDays = dayjs(dayjs().format('YYYY-MM-DD')).diff(
    dayjs(date),
    'day',
  );
  switch (differenceInFullDays) {
    case 0:
      return 'Tänään';
    case 1:
      return 'Eilen';
    default:
      return differenceInFullDays + ' päivää sitten';
  }
}

export default function RegulationFeed({
  feed,
  category,
}: RegulationFeedProps) {
  const { t } = useTranslation();
  const [filteredFeed, setFilteredFeed] = useState<GroupedRegulationFeedList>(
    [],
  );

  /* eslint-disable import-x/no-named-as-default-member */
  dayjs.extend(relativeTime);
  dayjs.locale('fi');

  useEffect(() => {
    setFilteredFeed(groupByDate(filterFeedByCategory(feed, category)));
  }, [category, feed]);
  return (
    <Stack
      useFlexGap
      sx={{
        gap: 2,
      }}
    >
      {filteredFeed.length === 0 ? (
        <Paper sx={{ padding: 2 }} variant='outlined'>
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {t('regulationFeed.noContent')}
          </Typography>
        </Paper>
      ) : (
        filteredFeed.map((group: GroupedRegulationFeedListItem) => {
          return (
            <Box key={'group-' + group.date}>
              <Typography
                variant='overline'
                sx={{
                  paddingX: 2,
                }}
              >
                {getDateTitle(group.date)}
              </Typography>
              <Stack
                useFlexGap
                sx={{
                  gap: 1,
                  marginTop: 1,
                }}
              >
                {group.items.map((item) => {
                  return (
                    <RegulationFeedItem
                      key={item.guid + uuidv4()}
                      title={item.title}
                      description={item.description}
                      link={item.link}
                    />
                  );
                })}
              </Stack>
            </Box>
          );
        })
      )}
    </Stack>
  );
}
